import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaPlus, FaTimes, FaSearch, FaSave, FaArrowLeft, FaCalendarAlt, FaBed, FaDumbbell, FaChevronDown, FaChevronUp, FaTrash } from 'react-icons/fa';
import { createUserProgram, updateUserProgram, getUserPrograms, getWorkouts, getUserWorkouts } from '../../services/fitnessService';
import { createCoachProgram, updateCoachProgram, getCoachWorkouts } from '../../services/coachFitnessService';
import toast from 'react-hot-toast';
import './WorkoutCreator.css';
import './ProgramCreator.css';

const DIFFICULTIES = ['beginner', 'intermediate', 'advanced'];

const toPositiveInt = (value, fallback) => {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const buildDaySlots = ({ durationWeeks, daysPerWeek, existingDays = [] }) => {
  const normalizedWeeks = toPositiveInt(durationWeeks, 4);
  const normalizedDaysPerWeek = toPositiveInt(daysPerWeek, 3);
  const totalDays = normalizedWeeks * normalizedDaysPerWeek;

  const existingByDayNumber = new Map(
    (existingDays || [])
      .filter(day => day && Number.isFinite(Number(day.dayNumber)))
      .map(day => [Number(day.dayNumber), day])
  );

  const slots = [];
  for (let dayNumber = 1; dayNumber <= totalDays; dayNumber += 1) {
    const week = Math.floor((dayNumber - 1) / normalizedDaysPerWeek) + 1;
    const dayInWeek = ((dayNumber - 1) % normalizedDaysPerWeek) + 1;
    const existing = existingByDayNumber.get(dayNumber);
    slots.push({
      dayNumber,
      dayName: existing?.dayName || `Week ${week} - Day ${dayInWeek}`,
      isRestDay: existing?.isRestDay || false,
      workout: existing?.workout || null,
      workoutId: existing?.workoutId || '',
      notes: existing?.notes || '',
      _workoutData: existing?._workoutData || null
    });
  }
  return slots;
};

export default function ProgramCreator({ isCoach = false }) {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!editId);

  // Program fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [goal, setGoal] = useState('');
  const [durationWeeks, setDurationWeeks] = useState(4);
  const [daysPerWeek, setDaysPerWeek] = useState(3);
  const [sessionDuration, setSessionDuration] = useState('30-45 minutes');
  const [difficulty, setDifficulty] = useState('beginner');
  const [targetAudience, setTargetAudience] = useState('');
  const [focus, setFocus] = useState('');
  const [equipmentNeeded, setEquipmentNeeded] = useState('');
  const [tags, setTags] = useState('');
  const [guidelines, setGuidelines] = useState('');
  const [progressionNotes, setProgressionNotes] = useState('');

  // Pricing (coach only)
  const [pricingType, setPricingType] = useState('free');
  const [price, setPrice] = useState('');
  const [accessType, setAccessType] = useState('public');

  // Day slots
  const [days, setDays] = useState([]);

  // Workout picker
  const [pickerOpenFor, setPickerOpenFor] = useState(null); // day index
  const [workoutSearch, setWorkoutSearch] = useState('');
  const [availableWorkouts, setAvailableWorkouts] = useState([]);
  const [workoutSearchResults, setWorkoutSearchResults] = useState([]);
  const [loadingWorkouts, setLoadingWorkouts] = useState(false);
  const pickerRef = useRef(null);

  // Generate day slots when durationWeeks/daysPerWeek change
  useEffect(() => {
    if (editId && loading) return; // Don't regenerate while loading edit data
    setDays(prev => buildDaySlots({
      durationWeeks,
      daysPerWeek,
      existingDays: prev
    }));
  }, [durationWeeks, daysPerWeek, editId, loading]);

  // Load workouts for the picker
  useEffect(() => {
    const loadWorkouts = async () => {
      setLoadingWorkouts(true);
      try {
        const [publicWorkoutsResponse, ownWorkoutsResponse] = await Promise.all([
          getWorkouts().catch(() => []),
          isCoach ? getCoachWorkouts().catch(() => []) : getUserWorkouts().catch(() => [])
        ]);
        const publicWorkouts = Array.isArray(publicWorkoutsResponse)
          ? publicWorkoutsResponse
          : publicWorkoutsResponse?.workouts || [];
        const ownWorkouts = Array.isArray(ownWorkoutsResponse)
          ? ownWorkoutsResponse
          : ownWorkoutsResponse?.workouts || [];
        // Merge and deduplicate by _id
        const all = [...ownWorkouts, ...publicWorkouts];
        const seen = new Set();
        const unique = all.filter(w => {
          if (seen.has(w._id)) return false;
          seen.add(w._id);
          return true;
        });
        setAvailableWorkouts(unique);
        setWorkoutSearchResults(unique);
      } catch (err) {
        console.error('Failed to load workouts:', err);
      } finally {
        setLoadingWorkouts(false);
      }
    };
    loadWorkouts();
  }, [isCoach]);

  // Filter workout search results
  useEffect(() => {
    if (!workoutSearch.trim()) {
      setWorkoutSearchResults(availableWorkouts);
      return;
    }
    const q = workoutSearch.toLowerCase();
    setWorkoutSearchResults(
      availableWorkouts.filter(w =>
        w.name?.toLowerCase().includes(q) ||
        w.category?.toLowerCase().includes(q) ||
        w.difficulty?.toLowerCase().includes(q)
      )
    );
  }, [workoutSearch, availableWorkouts]);

  // Load existing program for editing
  useEffect(() => {
    if (!editId) return;
    const load = async () => {
      try {
        if (isCoach) {
          const { getCoachPrograms } = await import('../../services/coachFitnessService');
          const coachProgramsResponse = await getCoachPrograms();
          const programs = Array.isArray(coachProgramsResponse)
            ? coachProgramsResponse
            : coachProgramsResponse?.programs || [];
          const program = programs.find(p => p._id === editId);
          if (program) populateForm(program);
        } else {
          const userProgramsResponse = await getUserPrograms();
          const programs = Array.isArray(userProgramsResponse)
            ? userProgramsResponse
            : userProgramsResponse?.programs || [];
          const program = programs.find(p => p._id === editId);
          if (program) populateForm(program);
        }
      } catch (err) {
        toast.error('Failed to load program');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [editId, isCoach]);

  const populateForm = (program) => {
    const programDurationWeeks = toPositiveInt(program.durationWeeks, 4);
    const programDaysPerWeek = toPositiveInt(program.daysPerWeek, 3);

    setName(program.name || '');
    setDescription(program.description || '');
    setGoal(program.goal || '');
    setDurationWeeks(programDurationWeeks);
    setDaysPerWeek(programDaysPerWeek);
    setSessionDuration(program.sessionDuration || '30-45 minutes');
    setDifficulty(program.difficulty || 'beginner');
    setTargetAudience(program.targetAudience || '');
    setFocus((program.focus || []).join(', '));
    setEquipmentNeeded((program.equipmentNeeded || []).join(', '));
    setTags((program.tags || []).join(', '));
    setGuidelines((program.guidelines || []).join('\n'));
    setProgressionNotes((program.progressionNotes || []).join('\n'));
    setPricingType(program.pricingType || 'free');
    setPrice(program.price ? (program.price / 100).toFixed(2) : '');
    setAccessType(program.accessType || 'public');

    const savedDays = Array.isArray(program.workouts)
      ? program.workouts.map(w => ({
          dayNumber: Number(w.dayNumber),
          dayName: w.dayName || `Day ${w.dayNumber}`,
          isRestDay: w.isRestDay || false,
          workout: w.workout?._id || w.workout || null,
          workoutId: w.workoutId || '',
          notes: w.notes || '',
          _workoutData: w.workout && typeof w.workout === 'object' ? w.workout : null
        }))
      : [];

    setDays(buildDaySlots({
      durationWeeks: programDurationWeeks,
      daysPerWeek: programDaysPerWeek,
      existingDays: savedDays
    }));
  };

  // Close picker on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpenFor(null);
        setWorkoutSearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Day helpers
  const updateDay = (index, field, value) => {
    setDays(prev => prev.map((d, i) => i === index ? { ...d, [field]: value } : d));
  };

  const toggleRestDay = (index) => {
    setDays(prev => prev.map((d, i) => i === index ? {
      ...d,
      isRestDay: !d.isRestDay,
      workout: !d.isRestDay ? null : d.workout,
      workoutId: !d.isRestDay ? '' : d.workoutId,
      _workoutData: !d.isRestDay ? null : d._workoutData
    } : d));
  };

  const assignWorkout = (dayIndex, workout) => {
    setDays(prev => prev.map((d, i) => i === dayIndex ? {
      ...d,
      workout: workout._id,
      workoutId: workout.workoutId,
      _workoutData: workout,
      isRestDay: false
    } : d));
    setPickerOpenFor(null);
    setWorkoutSearch('');
  };

  const clearWorkout = (dayIndex) => {
    setDays(prev => prev.map((d, i) => i === dayIndex ? {
      ...d, workout: null, workoutId: '', _workoutData: null
    } : d));
  };

  // Build payload
  const buildPayload = () => {
    const normalizedDurationWeeks = toPositiveInt(durationWeeks, 4);
    const normalizedDaysPerWeek = toPositiveInt(daysPerWeek, 3);

    return {
      name,
      description,
      goal,
      durationWeeks: normalizedDurationWeeks,
      daysPerWeek: normalizedDaysPerWeek,
      sessionDuration,
      difficulty,
      targetAudience,
      focus: focus.split(',').map(s => s.trim()).filter(Boolean),
      workouts: days.map(d => ({
        workout: d.workout,
        workoutId: d.workoutId,
        dayNumber: d.dayNumber,
        dayName: d.dayName,
        isRestDay: d.isRestDay,
        notes: d.notes
      })).filter(d => d.workout || d.isRestDay),
      guidelines: guidelines.split('\n').map(s => s.trim()).filter(Boolean),
      progressionNotes: progressionNotes.split('\n').map(s => s.trim()).filter(Boolean),
      equipmentNeeded: equipmentNeeded.split(',').map(s => s.trim()).filter(Boolean),
      tags: tags.split(',').map(s => s.trim()).filter(Boolean),
      ...(isCoach && { pricingType, price: pricingType === 'paid' ? Math.round(parseFloat(price || 0) * 100) : 0, accessType })
    };
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Program name is required');
      return;
    }
    if (!description.trim()) {
      toast.error('Description is required');
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload();
      if (editId) {
        if (isCoach) {
          await updateCoachProgram(editId, payload);
        } else {
          await updateUserProgram(editId, payload);
        }
        toast.success('Program updated!');
      } else {
        if (isCoach) {
          await createCoachProgram(payload);
        } else {
          await createUserProgram(payload);
        }
        toast.success('Program created!');
      }
      navigate(isCoach ? '/coach/dashboard' : '/fitness/programs');
    } catch (err) {
      toast.error(err.message || 'Failed to save program');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="program-creator">
        <div className="creator-loading">
          <div className="loading-spinner" />
          <p>Loading program...</p>
        </div>
      </div>
    );
  }

  const canSave = name.trim() && description.trim() && !saving;
  const normalizedDaysPerWeek = toPositiveInt(daysPerWeek, 1);

  // Group days by week for display
  const weeks = [];
  for (let i = 0; i < days.length; i += normalizedDaysPerWeek) {
    weeks.push(days.slice(i, i + normalizedDaysPerWeek));
  }

  return (
    <div className="program-creator">
      <div className="creator-container">
        <div className="creator-top-bar">
          <button type="button" className="back-btn" onClick={() => navigate(-1)}>
            <FaArrowLeft /> Back
          </button>
          <h1>{editId ? 'Edit Program' : 'Create Program'}</h1>
          <button type="button" className="save-btn" onClick={handleSave} disabled={!canSave}>
            <FaSave /> {saving ? 'Saving...' : 'Save Program'}
          </button>
        </div>

        {/* Basic Info */}
        <section className="creator-section">
          <h2>Program Details</h2>
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Program Name *</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. 8-Week Strength Builder" />
            </div>
            <div className="form-group full-width">
              <label>Description *</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="What is this program about?" rows={3} />
            </div>
            <div className="form-group">
              <label>Goal</label>
              <input type="text" value={goal} onChange={e => setGoal(e.target.value)} placeholder="e.g. Build functional strength" />
            </div>
            <div className="form-group">
              <label>Duration (weeks)</label>
              <input type="number" min="1" max="52" value={durationWeeks} onChange={e => setDurationWeeks(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Days per Week</label>
              <input type="number" min="1" max="7" value={daysPerWeek} onChange={e => setDaysPerWeek(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Session Duration</label>
              <input type="text" value={sessionDuration} onChange={e => setSessionDuration(e.target.value)} placeholder="30-45 minutes" />
            </div>
            <div className="form-group">
              <label>Difficulty</label>
              <select value={difficulty} onChange={e => setDifficulty(e.target.value)}>
                {DIFFICULTIES.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Target Audience</label>
              <input type="text" value={targetAudience} onChange={e => setTargetAudience(e.target.value)} placeholder="e.g. Beginners, office workers" />
            </div>
            <div className="form-group">
              <label>Focus Areas</label>
              <input type="text" value={focus} onChange={e => setFocus(e.target.value)} placeholder="glutes, core, cardio" />
            </div>
            <div className="form-group">
              <label>Equipment Needed</label>
              <input type="text" value={equipmentNeeded} onChange={e => setEquipmentNeeded(e.target.value)} placeholder="dumbbells, resistance bands" />
            </div>
            <div className="form-group full-width">
              <label>Tags</label>
              <input type="text" value={tags} onChange={e => setTags(e.target.value)} placeholder="beginner-friendly, home-gym" />
            </div>
          </div>
        </section>

        {/* Weekly Schedule */}
        <section className="creator-section">
          <h2><FaCalendarAlt /> Weekly Schedule</h2>
          <p className="section-hint">
            Assign workouts to each day. You can create workouts first, then assign them here.
          </p>

          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="week-block">
              <h3 className="week-title">Week {weekIndex + 1}</h3>
              <div className="week-days">
                {week.map((day, dayInWeek) => {
                  const globalIndex = weekIndex * normalizedDaysPerWeek + dayInWeek;
                  return (
                    <div key={globalIndex} className={`day-slot ${day.isRestDay ? 'rest-day' : ''} ${day._workoutData ? 'has-workout' : ''}`}>
                      <div className="day-slot-header">
                        <input
                          type="text"
                          className="day-name-input"
                          value={day.dayName}
                          onChange={e => updateDay(globalIndex, 'dayName', e.target.value)}
                        />
                        <button
                          type="button"
                          className={`rest-toggle ${day.isRestDay ? 'active' : ''}`}
                          onClick={() => toggleRestDay(globalIndex)}
                          title={day.isRestDay ? 'Remove rest day' : 'Mark as rest day'}
                        >
                          <FaBed />
                        </button>
                      </div>

                      {day.isRestDay ? (
                        <div className="day-rest-label">Rest Day</div>
                      ) : day._workoutData ? (
                        <div className="day-workout-card">
                          <div className="day-workout-info">
                            <FaDumbbell />
                            <div>
                              <div className="day-workout-name" title={day._workoutData.name}>{day._workoutData.name}</div>
                              <div className="day-workout-meta">
                                {day._workoutData.estimatedDuration && <span>{day._workoutData.estimatedDuration} min</span>}
                                {day._workoutData.difficulty && <span>{day._workoutData.difficulty}</span>}
                              </div>
                            </div>
                          </div>
                          <button type="button" className="clear-workout-btn" onClick={() => clearWorkout(globalIndex)}>
                            <FaTimes />
                          </button>
                        </div>
                      ) : (
                        <div className="day-workout-picker-area">
                          <button
                            type="button"
                            className="assign-workout-btn"
                            onClick={() => {
                              setPickerOpenFor(pickerOpenFor === globalIndex ? null : globalIndex);
                              setWorkoutSearch('');
                            }}
                          >
                            <FaPlus /> Assign Workout
                          </button>

                          {pickerOpenFor === globalIndex && (
                            <div className="workout-picker-dropdown" ref={pickerRef}>
                              <div className="picker-search">
                                <FaSearch />
                                <input
                                  type="text"
                                  placeholder="Search workouts..."
                                  value={workoutSearch}
                                  onChange={e => setWorkoutSearch(e.target.value)}
                                  autoFocus
                                />
                              </div>
                              <div className="picker-results">
                                {loadingWorkouts && <div className="picker-loading">Loading workouts...</div>}
                                {!loadingWorkouts && workoutSearchResults.length === 0 && (
                                  <div className="picker-empty">No workouts found. Create some first!</div>
                                )}
                                {workoutSearchResults.map(w => (
                                  <button
                                    key={w._id}
                                    type="button"
                                    className="picker-result-item"
                                    onClick={() => assignWorkout(globalIndex, w)}
                                  >
                                    <div className="picker-result-name">{w.name}</div>
                                    <div className="picker-result-meta">
                                      {w.estimatedDuration && <span>{w.estimatedDuration} min</span>}
                                      {w.difficulty && <span>{w.difficulty}</span>}
                                      {w.category && <span>{w.category}</span>}
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <input
                        type="text"
                        className="day-notes-input"
                        placeholder="Day notes..."
                        value={day.notes}
                        onChange={e => updateDay(globalIndex, 'notes', e.target.value)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </section>

        {/* Guidelines & Progression */}
        <section className="creator-section">
          <h2>Guidelines & Progression</h2>
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Guidelines (one per line)</label>
              <textarea value={guidelines} onChange={e => setGuidelines(e.target.value)} placeholder="e.g.&#10;Rest 60-90 seconds between sets&#10;Focus on form over weight" rows={4} />
            </div>
            <div className="form-group full-width">
              <label>Progression Notes (one per line)</label>
              <textarea value={progressionNotes} onChange={e => setProgressionNotes(e.target.value)} placeholder="e.g.&#10;Increase weight by 5% each week&#10;Add one rep per week for bodyweight moves" rows={4} />
            </div>
          </div>
        </section>

        {isCoach && (
          <section className="creator-section">
            <h3>Access</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Access</label>
                <select value={accessType} onChange={e => setAccessType(e.target.value)}>
                  <option value="public">Public — anyone can see & enroll</option>
                  <option value="invite_only">Invite Only — only your clients</option>
                </select>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
